import React from 'react';
import {
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Switch,
    TextField,
    Typography,
} from '@material-ui/core';
import SubSectionHeading from './SubSectionHeading';
import { providerSupportsStop } from '../../util/provider';
import { TIMER_UNITS, DEFAULT_TIMER_UNIT } from '../utils/autoTeardownUnits';

// Mirrors MIN_MINUTES in the API's nodus-core/src/libs/idleTeardown.js. Below
// this a quiet window stops being evidence of anything - somebody reading a
// paper between two commands would lose their cluster - so the API raises
// anything shorter rather than honouring it, and the form should not offer it.
export const MIN_IDLE_MINUTES = 15;

// Filled in when the switch goes on, so the section opens valid. Two hours of
// nobody at all is a real absence rather than a coffee break.
const DEFAULT_IDLE_VALUE = 2;

/**
 * Cluster-idle teardown.
 *
 * Stops or destroys the cluster once NOBODY is using it: no SSH session, no
 * attached remote desktop, no queued or running job, nothing burning CPU or
 * GPU, on the head or on any compute node.
 *
 * Sits beside the Auto Stop / Destroy Timer inside ClusterShutdownSection, and
 * stays a separate control rather than being folded into it: a lifetime and an
 * idle rule are different promises, and a cluster can carry both. What they
 * share is the question "when does this switch itself off", which is why they
 * share a card.
 */
export default function ClusterIdleSection({ form, setField, provider, disabled }) {
    const enabled = form.idleTeardownEnabled || false;
    const canStop = providerSupportsStop(provider);
    const action = canStop ? (form.idleTeardownAction || 'stop') : 'destroy';
    const value = form.idleTeardownValue ?? '';
    const unit = form.idleTeardownUnit || DEFAULT_TIMER_UNIT;

    // A provider that cannot stop has only one possible action - keep the
    // stored value honest so the form never submits a stop it cannot honour.
    React.useEffect(() => {
        if (!canStop && form.idleTeardownAction !== 'destroy') {
            setField('idleTeardownAction', 'destroy');
        }
    }, [canStop, form.idleTeardownAction, setField]);

    const [touched, setTouched] = React.useState(false);
    const numericValue = Number(value);
    const factor = (TIMER_UNITS.find(u => u.value === unit) || { factor: 60 }).factor;
    const minutes = numericValue * factor;
    const empty = enabled && touched && (value === '' || !isFinite(numericValue) || numericValue <= 0);
    const tooShort = enabled && touched && !empty && minutes < MIN_IDLE_MINUTES;

    const handleToggle = (checked) => {
        setTouched(false);
        const needsDefault = checked && (value === '' || value === null || value === undefined);
        setField(needsDefault
            ? {
                idleTeardownEnabled: true,
                idleTeardownValue: DEFAULT_IDLE_VALUE,
                idleTeardownUnit: form.idleTeardownUnit || DEFAULT_TIMER_UNIT,
            }
            : { idleTeardownEnabled: checked });
    };

    return (
        <div>
            <SubSectionHeading
                icon="person_outline"
                title="Shut Down When Nobody Is Using It"
                tooltip="Detects users rather than elapsed time: SSH sessions, attached remote desktops, queued and running jobs, and CPU, GPU or I/O activity on any node. The countdown restarts on any activity, or on any source that cannot be verified."
            />
            <FormControlLabel
                control={
                    <Switch
                        checked={enabled}
                        onChange={(event) => handleToggle(event.target.checked)}
                        name="idleTeardownEnabled"
                        color="primary"
                        disabled={disabled}
                    />
                }
                label={enabled ? 'Enabled' : 'Disabled'}
            />
            <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 16 }}>
                Automatically {action === 'destroy' ? 'destroys' : 'stops'} this cluster after the period set below
                with no users connected and no work running. Requires the platform-wide switch in Settings &gt; Cluster.
            </Typography>

            {enabled && (
                <>
                    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', marginBottom: 16 }}>
                        <TextField
                            id="idleTeardownValue"
                            label="Unused for"
                            type="number"
                            variant="outlined"
                            value={value}
                            onChange={(event) => {
                                setTouched(true);
                                setField('idleTeardownValue', event.target.value);
                            }}
                            onBlur={() => setTouched(true)}
                            inputProps={{ min: 1, step: 1 }}
                            error={empty || tooShort}
                            helperText={
                                empty ? 'Enter a number greater than 0'
                                    : tooShort ? `At least ${MIN_IDLE_MINUTES} minutes`
                                        : ' '
                            }
                            disabled={disabled}
                            style={{ width: 160 }}
                        />
                        <FormControl variant="outlined" style={{ width: 160 }}>
                            <InputLabel id="idleTeardownUnit-label">Unit</InputLabel>
                            <Select
                                labelId="idleTeardownUnit-label"
                                id="idleTeardownUnit"
                                value={unit}
                                onChange={(event) => setField('idleTeardownUnit', event.target.value)}
                                label="Unit"
                                disabled={disabled}
                            >
                                {TIMER_UNITS.map(u => (
                                    <MenuItem key={u.value} value={u.value}>{u.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>

                    <Typography variant="subtitle2" style={{ marginBottom: 4 }}>
                        When nobody has used it for that long
                    </Typography>
                    <RadioGroup
                        name="idleTeardownAction"
                        value={action}
                        onChange={(event) => setField('idleTeardownAction', event.target.value)}
                    >
                        <FormControlLabel
                            value="stop"
                            control={<Radio color="primary" />}
                            disabled={disabled || !canStop}
                            label={
                                <span>
                                    Stop the cluster
                                    <Typography variant="caption" color="textSecondary" display="block">
                                        {canStop
                                            ? 'Powers off the head node. Compute billing stops, storage is retained and the cluster can be restarted.'
                                            : 'Not available: this provider cannot stop a cluster, only destroy it.'}
                                    </Typography>
                                </span>
                            }
                        />
                        <FormControlLabel
                            value="destroy"
                            control={<Radio color="primary" />}
                            disabled={disabled}
                            label={
                                <span>
                                    Destroy the cluster
                                    <Typography variant="caption" color="textSecondary" display="block">
                                        Destroys the cluster and its local storage. Cluster Protection prevents this.
                                    </Typography>
                                </span>
                            }
                        />
                    </RadioGroup>

                    {/* Only offered alongside a stop: a destroyed cluster has
                        nothing left to start, so the pair is a contradiction
                        rather than a configuration. */}
                    {action === 'stop' && (
                        <FormControlLabel
                            style={{ marginTop: 8 }}
                            control={
                                <Switch
                                    checked={form.idleTeardownStartOnLogin || false}
                                    onChange={(event) => setField('idleTeardownStartOnLogin', event.target.checked)}
                                    name="idleTeardownStartOnLogin"
                                    color="primary"
                                    disabled={disabled}
                                />
                            }
                            label={
                                <span>
                                    Start it again when someone signs in
                                    <Typography variant="caption" color="textSecondary" display="block">
                                        Starts the cluster when a user with access signs in, or a student joins a
                                        workshop that uses it. Applies only to clusters stopped by this feature.
                                    </Typography>
                                </span>
                            }
                        />
                    )}
                </>
            )}
        </div>
    );
}
